import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBranding, resolveAsset } from "@/lib/branding";
import logoGoodBox from "@/assets/logo-goodbox-white.png";

const DEMO = [
  { role: "Super Admin", email: "paul.grundy@goodnus.com", password: "Admin@123" },
  { role: "Finance", email: "finance@goodnus.com", password: "Finance@123" },
  { role: "Kiosk Manager", email: "manager@goodnus.com", password: "Manager@123" },
];

export default function Login() {
  const { login } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const loginBg = branding?.backgrounds?.login ? resolveAsset(branding.backgrounds.login) : null;
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const showDemo = host.includes("preview.emergentagent.com") || host === "localhost" || host === "127.0.0.1";
  const [email, setEmail] = useState(showDemo ? "paul.grundy@goodnus.com" : "");
  const [password, setPassword] = useState(showDemo ? "Admin@123" : "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const r = await login(email.trim(), password);
      if (r?.twofa_required) {
        localStorage.setItem("hv_pending_2fa", r.twofa_token);
        startGoogleOAuth();
        return;
      }
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleError = new URLSearchParams(window.location.search).get("google_error");
  const twofaError = new URLSearchParams(window.location.search).get("twofa_error");
  const startGoogleOAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google";
    const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem("hv_oauth_state", state);
    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      state,
      access_type: "online",
      prompt: "select_account",
      include_granted_scopes: "true",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };
  const googleLogin = () => startGoogleOAuth();

  return (
    <div className="min-h-screen flex bg-slate-950">
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 text-white relative overflow-hidden" style={{ background: "var(--brand-gradient, linear-gradient(135deg,#1e3a8a,#0f172a))" }}>
        {loginBg && <div className="absolute inset-0 opacity-30 bg-cover bg-center" style={{ backgroundImage: `url(${loginBg})` }} />}
        <div className="relative flex items-center">
          <img src={branding?.logo_url ? resolveAsset(branding.logo_url) : logoGoodBox} alt={branding?.brand_name || "The Good Box"} className="w-auto" style={{ height: "calc(28px * var(--brand-logo-scale, 1))", maxHeight: "56px" }} />
        </div>
        <div className="relative">
          <h1 className="font-admin-head text-4xl font-extrabold leading-tight whitespace-pre-line">{branding?.hero?.title || "Run every kiosk from one place."}</h1>
          <p className="mt-4 text-slate-300 max-w-md">{branding?.hero?.subtitle || "Manage products, stock and reporting across your estate."}</p>
        </div>
        <p className="relative text-slate-500 text-sm">{(branding?.brand_name || "The Good Box")} · vend.thegoodbox.com</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <h2 className="font-admin-head text-2xl font-bold text-white">Welcome back</h2>
          <p className="text-slate-400 text-sm mt-1">Sign in to the administration portal</p>

          {googleError && <p data-testid="google-login-error" className="mt-6 text-sm text-red-400">Google sign-in failed or no account is linked to that email.</p>}
          <button
            type="button"
            data-testid="google-signin-button"
            onClick={googleLogin}
            className="mt-8 w-full h-11 rounded-md bg-white text-slate-800 font-medium flex items-center justify-center gap-2.5 hover:bg-slate-100 transition-colors"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </button>

          <div className="mt-4 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-800" />
            <span className="text-xs text-slate-500">or</span>
            <div className="h-px flex-1 bg-slate-800" />
          </div>

          <form onSubmit={submit} className="mt-4 space-y-4">
            <div>
              <Label className="text-slate-300">Email</Label>
              <Input data-testid="admin-login-username-input" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-slate-900 border-slate-700 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Password</Label>
              <Input data-testid="admin-login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-slate-900 border-slate-700 text-white" />
            </div>
            {error && <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>}
            {twofaError && <p data-testid="twofa-error" className="text-sm text-red-400">Two-factor check failed — the Google account must match your login email. Please sign in and try again.</p>}
            <Button data-testid="admin-login-submit-button" type="submit" disabled={loading} className="w-full h-11 bg-blue-600 hover:bg-blue-700">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          {showDemo && (
            <div className="mt-8 border-t border-slate-800 pt-5">
              <p className="text-xs text-slate-500 mb-2">Demo accounts (tap to fill)</p>
              <div className="space-y-2">
                {DEMO.map((d) => (
                  <button
                    key={d.email}
                    data-testid={`demo-account-${d.role.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => { setEmail(d.email); setPassword(d.password); }}
                    className="w-full text-left rounded-lg bg-slate-900 border border-slate-800 px-3 py-2 hover:border-blue-600 transition-colors"
                  >
                    <span className="text-sm font-medium text-white">{d.role}</span>
                    <span className="block text-xs text-slate-500">{d.email}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
